import type DataLoader from "dataloader";
import { z } from "zod";
import type { LessonLimitation } from "./lesson";
import type { ProjectItemCountResult } from "./project";
import type { Slide } from "./slide";

/**
 * Session Context for GraphQL Resolvers
 * Uses better-auth for all platforms (web + mobile)
 */

/** One source lesson's contribution to the accumulated known-set (display-only). */
export interface AccumulatedLimitGroup {
  lessonId: string;
  lessonName: string;
  projectName: string | null;
  words: string[];
  grammar: string[];
}

/**
 * The accumulated known-set returned by the limit dataloaders / `getAccumulatedLimit`.
 * `wordLimit`/`grammarLimit` are the flat de-duplicated union the conversation consumes; `byLesson`
 * is a parallel per-lesson breakdown for display, in curriculum order.
 */
export interface AccumulatedLimit {
  wordLimit: string[];
  grammarLimit: string[];
  byLesson: AccumulatedLimitGroup[];
}

// Zod schema for Better Auth user validation
export const BetterAuthUserSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  emailVerified: z.boolean().default(false),
  name: z.string(),
  username: z.string(),
  displayUsername: z.string().optional(),
  role: z.enum([
    // Admin roles
    "super",
    "admin",
    "editor",
    "developer",
    "educational_cons",
    // School roles
    "principal",
    "teacher",
    "student",
    "secretary",
    // Legacy
    "superuser",
  ]),
  schoolId: z.string().optional(),
  // mybestuser OAuth2 fields
  authSource: z.enum(["local", "mybestuser"]).default("local"),
  myBestUserId: z.string().nullable().default(null),
  myBestUserRole: z.string().nullable().default(null),
  // Contract expiry (ISO string, stored in user doc at login)
  ticketExpiresAt: z.string().nullable().optional(),
  // Product access from MyBestUser tickets (populated at login)
  // null  = legacy session (field absent in DB) → no filter applied (grace period)
  // []    = validated at login, user has no active tickets → block all content
  // [...] = validated at login, restrict to these product IDs
  productIds: z.array(z.string()).nullable().optional().default(null),
  editableProductIds: z.array(z.string()).nullable().optional().default(null),
  // maxLevel per product (productId → maxLevel). null = legacy session (no filter).
  productMaxLevels: z.record(z.string(), z.number()).nullable().optional().default(null),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
});

// Better-auth user type (inferred from Zod schema)
export type BetterAuthUser = z.infer<typeof BetterAuthUserSchema>;

// Better-auth session type
export interface BetterAuthSession {
  id: string;
  userId: string;
  expiresAt: Date;
}

// Combined session data from better-auth
export interface SessionData {
  user: BetterAuthUser;
  session: BetterAuthSession;
}

export interface LinkedCopyKey {
  itemId: string;
  model: "project" | "lesson" | "slide";
}

// Scoped by viewer role: a student and an editor see different children of the same parent, and
// a shared cache key would leak one into the other.
export interface SlideChildrenKey {
  parentId: string;
  isEditor: boolean;
}

export interface LinkedCopyInfo {
  id: string;
  name: string;
}

// DataLoaders interface
// Note: Using unknown for DataLoader to avoid circular dependency with dataloader package
export interface DataLoaders {
  userLoader: unknown; // DataLoader<string, IUser | null>
  itemCountLoader?: DataLoader<string, number>; // DataLoader<string, number>
  visibleItemCountLoader?: DataLoader<string, ProjectItemCountResult>; // DataLoader<string, ProjectItemCountResult> - counts only visible (non-hidden) child items
  imageUrlLoader?: DataLoader<string, string | null>;
  imageDisplayUrlLoader?: DataLoader<string, string | null>; // Maps image ID → displayUrl (null if no display version)
  audioUrlLoader?: DataLoader<string, string | null>; // Maps audio ID → signed CDN URL
  mediaAssetLoader?: DataLoader<string, string | null>; // DataLoader for Media House MediaAsset URLs
  hasChildProjectsLoader?: DataLoader<string, boolean>; // DataLoader<string, boolean> - checks if project has child projects (non-deleted, non-trash)
  slidesLimitationLoader?: DataLoader<string, LessonLimitation>; // DataLoader<string, boolean> - checks if project has child projects (non-deleted, non-trash)
  lessonAccumulatedLimitLoader?: DataLoader<string, AccumulatedLimit>; // Maps lessonId → accumulated known-set (every form slide up to + including the lesson)
  slideInheritedLimitLoader?: DataLoader<string, AccumulatedLimit>; // Maps slideId → inherited known-set (every form slide strictly before the slide)
  activeSlideIdsLoader?: DataLoader<string, string[]>; // DataLoader<string, string> - returns all of active slides of a lesson (isTrash: false , isDeleted: false, isHidden:false)
  activeSlideCountLoader?: DataLoader<string, number>; // DataLoader<string, string> - returns number of active slides of a lesson (isTrash: false , isDeleted: false, isHidden:false)
  projectHasLinkedCopyCheckLoader?: DataLoader<string, boolean>; // DataLoader<string, boolean> - checks if project has other project linked to this project
  lessonHasLinkedCopyCheckLoader?: DataLoader<string, boolean>; // DataLoader<string, boolean> - checks if lesson has other lesson linked to this lesson
  slideHasLinkedCopyCheckLoader?: DataLoader<string, boolean>; // DataLoader<string, boolean> - checks if slide has other slide linked to this slde
  slideChildrenLoader?: DataLoader<SlideChildrenKey, Slide[], string>; // Maps a main slide → its subslides, in sortOrder
  linkedCopyIdsLoader?: DataLoader<LinkedCopyKey, LinkedCopyInfo[]>; // Returns linked copy id and name for a given item (project, lesson, or slide)
  lessonViewNameLoader?: DataLoader<string, string>; // Returns lesson name for lessonView
  lessonViewProjectPathLoader?: DataLoader<string, string[]>; // Returns full ancestor project path for lessonView (root → direct parent)
  lessonBreadcrumbLoader?: DataLoader<string, string[]>; // Maps lessonId → its project ancestors then the lesson name
  slideViewNameLoader?: DataLoader<string, string>; // Returns slide name for slideView
  slideSectionCountsLoader?: DataLoader<
    string,
    {
      type: string;
      knowledgeArea: string;
      template: string | null;
      skill: string;
      count: number;
    }[]
  >; // DataLoader<string, SlideSectionCount[]> - returns slide counts grouped by skill for a lesson
  slideTagCountsLoader?: DataLoader<string, { tagId: string; count: number }[]>; // Maps lessonId → per-tag slide counts
  audioLoader?: DataLoader<string, AudioRecord | null>; // Maps audio ID → the Audio document backing an audio track
  audioTrackHasPlayedLoader?: DataLoader<AudioTrackPlayKey, boolean, string>; // Has this student finished this track?
}

/** Key for the per-student audio track play lookup. */
export interface AudioTrackPlayKey {
  audioTrackId: string;
  userId: string;
}

/**
 * The subset of an Audio document the GraphQL layer needs. `url` is derived from `key`
 * by the Audio field resolver, so only `key` has to be carried here.
 */
export interface AudioRecord {
  id: string;
  key: string;
  filename: string;
  size: number;
  mimeType: string;
  audioText: string | null;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * GraphQL Resolver Context
 * Contains session data and dataloaders
 */
export interface SessionContext {
  /**
   * Session data from better-auth
   * Present when user is authenticated (web or mobile)
   * undefined when unauthenticated
   */
  session?: SessionData;

  /**
   * DataLoaders for batching database queries
   * Always present regardless of authentication state
   */
  dataloaders: DataLoaders;

  /**
   * True when the request was authenticated via session cookie (web), false when
   * authenticated via Authorization: Bearer token (mobile, no shared cookie jar).
   * Drives whether media URLs are returned bare (cookie-gated) or pre-signed.
   */
  isWeb: boolean;

  /**
   * Which caller the API's abuse controls attribute this request to. Computed in the Express
   * layer, where the trusted client address is available, and carried here so response-time
   * plugins can meter against the same identity as the rate limiters.
   *
   * Contains a session token when the caller has one. Treat it as a credential: never log it.
   */
  abuseKey?: string;
}
